import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { Capacitor } from "@capacitor/core";
import { config } from "../config";

const ACCESS_TOKEN_KEY = "token";
const REFRESH_TOKEN_KEY = "refresh_token";

const api = axios.create({
  baseURL: config.apiUrl,
  withCredentials: true,
});

/** Rotte visitabili senza login (es. QR code → biglietto pubblico). */
const PUBLIC_APP_PATH_PREFIXES = [
  "/card/",
  "/register",
  "/landing",
  "/verify-email",
  "/forgot-password",
  "/reset-password",
  "/set-password",
];

export function isNativeApp(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

export function getAuthToken(): string | null {
  try {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!token || token === "null" || token === "undefined") return null;
    const trimmed = token.trim();
    return trimmed.length > 0 ? trimmed : null;
  } catch {
    return null;
  }
}

function getStoredRefreshToken(): string | null {
  try {
    const token = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!token || token === "null" || token === "undefined") return null;
    const trimmed = token.trim();
    return trimmed.length > 0 ? trimmed : null;
  } catch {
    return null;
  }
}

/** Persist session after login / register / setup / refresh. */
export function persistAuthSession(data: {
  access_token?: string;
  refresh_token?: string | null;
}): void {
  if (data.access_token) {
    localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
  }
  // Web HTTPS: refresh lives in HttpOnly cookie only.
  // Native (and local HTTP): backend also returns refresh_token in body.
  if (data.refresh_token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
  } else if (!isNativeApp()) {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
}

export function clearAuthSession(): void {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {}
}

function isPublicAppPath(path: string): boolean {
  return PUBLIC_APP_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/** GET /cards/:id — endpoint pubblico (non /cards/me). */
function isPublicCardReadRequest(url?: string): boolean {
  if (!url) return false;
  return /\/cards\/[a-f\d]{24}$/i.test(url.split("?")[0]);
}

function attachClientPlatform(headers: Record<string, string>) {
  // Marks this build as refresh-token capable (web + new native builds).
  // Old store apps omit it → backend keeps issuing long-lived access tokens.
  headers["X-Auth-Client"] = "v2";
  if (isNativeApp()) {
    headers["X-Client-Platform"] = "capacitor";
  }
}

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const body: { refresh_token?: string } = {};
        const stored = getStoredRefreshToken();
        if (stored) {
          body.refresh_token = stored;
        }
        const res = await axios.post(
          `${config.apiUrl}/refresh`,
          body,
          {
            withCredentials: true,
            headers: {
              "X-Auth-Client": "v2",
              ...(isNativeApp() ? { "X-Client-Platform": "capacitor" } : {}),
            },
          },
        );
        persistAuthSession(res.data);
        return res.data.access_token as string;
      } catch {
        clearAuthSession();
        return null;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

api.interceptors.request.use((reqConfig) => {
  const cfg = reqConfig as InternalAxiosRequestConfig & {
    public?: boolean;
    skipAuthHeader?: boolean;
  };
  const headers = (cfg.headers || {}) as Record<string, string>;
  attachClientPlatform(headers);

  if (cfg.public || cfg.skipAuthHeader) {
    delete headers.Authorization;
    cfg.headers = headers as any;
    return reqConfig;
  }

  try {
    const token = getAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  } catch {}
  cfg.headers = headers as any;
  return reqConfig;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      skipAuthRedirect?: boolean;
      public?: boolean;
      skipAuthHeader?: boolean;
    };

    if (error?.response?.status === 401 && original && !original._retry) {
      const url = String(original.url || "");
      const isAuthEndpoint =
        url.includes("/login") ||
        url.includes("/register") ||
        url.includes("/refresh") ||
        url.includes("/logout");

      if (!isAuthEndpoint && !original.public && !original.skipAuthHeader) {
        original._retry = true;
        const newToken = await refreshAccessToken();
        if (newToken) {
          original.headers = original.headers || {};
          (original.headers as any).Authorization = `Bearer ${newToken}`;
          return api(original);
        }
      }
    }

    if (error?.response?.status === 401) {
      clearAuthSession();
      if (original?.skipAuthRedirect) {
        return Promise.reject(error);
      }

      const path = window.location.pathname;
      const requestUrl = String(original?.url || "");

      if (isPublicAppPath(path) || isPublicCardReadRequest(requestUrl)) {
        return Promise.reject(error);
      }

      const detail = (error?.response?.data as any)?.detail;
      if (detail === "TEMP_EXPIRED" && path !== "/forgot-password") {
        window.location.href = "/forgot-password";
        return Promise.reject(error);
      }
      if (path !== "/" && path !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

export async function logoutRequest(): Promise<void> {
  const body: { refresh_token?: string } = {};
  const stored = getStoredRefreshToken();
  if (stored) body.refresh_token = stored;
  try {
    await api.post("/logout", body, { skipAuthRedirect: true } as any);
  } catch {
    // Logout must never block local cleanup
  } finally {
    clearAuthSession();
  }
}

export default api;
